import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MessageSquare, MonitorUp, Power, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import prontuarioTrayIconSvg from '@/assets/icons/daily-tray-prontuario.svg?raw';
import { PRONTUARIO_TRAY_ACTIVE_COLOR } from '@/constants/sessionCallDailyTheme';
import {
	DailyCallDemonstracaoPeopleChatPanel,
	type DailyCallSidePanel,
} from './DailyCallDemonstracaoSidePanel';
import { DailyCamIcon, DailyMicIcon } from './dailyTutorialMediaIcons';
import {
	TutorialDemonstracaoCallCoachBalloon,
	TUTORIAL_DEMO_END_CALL_SELECTOR,
	TUTORIAL_DEMO_MEETING_TIMER_SELECTOR,
	TUTORIAL_DEMO_PRONTUARIO_CONTENT_SELECTOR,
	TUTORIAL_DEMO_PRONTUARIO_FORMATTING_HELP_SELECTOR,
	TUTORIAL_DEMO_PRONTUARIO_NOTES_SELECTOR,
	TUTORIAL_DEMO_PRONTUARIO_OPEN_SELECTORS,
	TUTORIAL_DEMO_PRONTUARIO_PATIENT_CARD_SELECTOR,
	TUTORIAL_DEMO_PRONTUARIO_PATIENT_DETAIL_SELECTOR,
	TUTORIAL_DEMO_PRONTUARIO_SESSIONS_LIST_SELECTOR,
	TUTORIAL_DEMO_PRONTUARIO_SESSIONS_OPEN_SELECTOR,
} from './TutorialDemonstracaoCallCoachBalloon';
import { TutorialDemonstracaoCallWelcomeCoachBalloon } from './TutorialDemonstracaoCallWelcomeCoachBalloon';
import type { SessionCallProntuarioView } from '@/pages/professional/sessao-chamada/components/prontuario/SessionCallProntuarioViewTransition';

const DAILY_PANEL_BG = '#121A24';
const DAILY_SURFACE = '#2B3F56';
const DAILY_LAYOUT_INACTIVE_BG = '#3D5166';

const prontuarioTrayIconDataUri = `data:image/svg+xml,${encodeURIComponent(
	prontuarioTrayIconSvg.trim(),
)}`;

const DailyPresenterLayoutIcon = () => (
	<svg viewBox="0 0 24 24" className="size-[22px]" aria-hidden>
		<path
			d="M5.5 8.5C5.5 7.94772 5.94772 7.5 6.5 7.5H17.5C18.0523 7.5 18.5 7.94772 18.5 8.5V15.5C18.5 16.0523 18.0523 16.5 17.5 16.5H6.5C5.94772 16.5 5.5 16.0523 5.5 15.5V8.5Z"
			fill="none"
			stroke="currentColor"
		/>
		<line x1="14.5" y1="8" x2="14.5" y2="16" stroke="currentColor" />
		<line x1="15" y1="10.5" x2="18" y2="10.5" stroke="currentColor" />
		<line x1="15" y1="13.5" x2="18" y2="13.5" stroke="currentColor" />
	</svg>
);

const DailyGridLayoutIcon = () => (
	<svg viewBox="0 0 24 24" className="size-[22px]" aria-hidden>
		<path
			d="M6 8.5C6 7.94772 6.44772 7.5 7 7.5H17C17.5523 7.5 18 7.94772 18 8.5V15.5C18 16.0523 17.5523 16.5 17 16.5H7C6.44772 16.5 6 16.0523 6 15.5V8.5Z"
			fill="none"
			stroke="currentColor"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M9.5 16V14H6.5V13H9.5V11H6.5V10H9.5V8H10.5V10H13.5V8H14.5V10H17.5V11H14.5V13H17.5V14H14.5V16H13.5V14H10.5V16H9.5ZM13.5 13V11H10.5V13H13.5Z"
			fill="currentColor"
		/>
	</svg>
);

type DailyTrayButtonProps = {
	label: string;
	onClick?: () => void;
	isActive?: boolean;
	danger?: boolean;
	compact?: boolean;
	tutorialDemoTarget?: string;
	children: ReactNode;
};

type DailyDeviceTrayButtonProps = {
	label: string;
	variant: 'camera' | 'mic';
	isOn: boolean;
	onClick?: () => void;
};

const DailyDeviceTrayButton = ({
	label,
	variant,
	isOn,
	onClick,
}: DailyDeviceTrayButtonProps) => (
	<button
		type="button"
		onClick={onClick}
		className={cn(
			'group flex min-w-[4.75rem] flex-col items-center justify-center',
			'rounded-md px-2 py-1',
			'text-[11px] font-normal text-white transition-colors',
			'hover:bg-[#3D5166]',
		)}
	>
		<span
			className={cn(
				'flex size-6 items-center justify-center rounded-sm',
				!isOn && 'bg-[#ea4335]',
			)}
		>
			{variant === 'camera' ? (
				<DailyCamIcon muted={!isOn} className="size-5" />
			) : (
				<DailyMicIcon muted={!isOn} className="size-5" />
			)}
		</span>

		<span className="mt-0.5 whitespace-nowrap leading-none text-white/90">
			{label}
		</span>
	</button>
);

const DailyTrayButton = ({
	label,
	onClick,
	isActive = false,
	danger = false,
	compact = false,
	tutorialDemoTarget,
	children,
}: DailyTrayButtonProps) => (
	<button
		type="button"
		onClick={onClick}
		data-tutorial-demo={tutorialDemoTarget}
		className={cn(
			'group relative flex flex-col items-center justify-center px-1.5 text-[11px] font-medium text-slate-200 transition-colors hover:text-white focus:outline-none',
			compact ? 'min-w-[3.75rem] gap-0.5' : 'min-w-[4.5rem] gap-1 py-1',
			isActive && 'text-white',
		)}
	>
		<span
			className={cn(
				'flex items-center justify-center rounded-md',
				compact ? 'size-6' : 'size-8',
				danger && 'text-red-400',
			)}
		>
			{children}
		</span>
		<span className="whitespace-nowrap leading-none">{label}</span>
		{isActive ? (
			<span
				className="absolute -bottom-1 left-0 h-0.5 w-full rounded-full"
				style={{ backgroundColor: PRONTUARIO_TRAY_ACTIVE_COLOR }}
				aria-hidden
			/>
		) : null}
	</button>
);

const LOCAL_PIP_TILE_CLASS =
	'absolute right-3 top-3 z-20 aspect-video w-[min(28%,220px)] min-w-[140px] shadow-lg';

type LayoutMode = 'presenter' | 'grid';

const TileNameBadge = ({
	name,
	isLocal,
	extra,
}: {
	name: string;
	isLocal?: boolean;
	extra?: string;
}) => (
	<div className="absolute bottom-0 left-0 z-10 max-w-full bg-black/60 px-1.5 py-1">
		<span className="truncate text-[11px] leading-none text-white">
			{name}
			{isLocal ? <span className="text-white/70"> (Você)</span> : null}
			{extra ? <span className="text-white/70">{extra}</span> : null}
		</span>
	</div>
);

const RemoteParticipantTile = ({
	centerLabel,
	badgeName,
	className,
}: {
	centerLabel: string;
	badgeName: string;
	className?: string;
}) => (
	<div
		className={cn(
			'relative flex min-h-0 min-w-0 items-center justify-center overflow-hidden',
			className,
		)}
		style={{ backgroundColor: DAILY_SURFACE }}
	>
		<p className="px-4 text-center text-sm font-medium uppercase tracking-wide text-white/90">
			{centerLabel}
		</p>
		<TileNameBadge name={badgeName} />
	</div>
);

const LocalMediaVideo = ({
	stream,
	active,
	mirrored = true,
	className,
	objectFit = 'contain',
}: {
	stream: MediaStream | null;
	active: boolean;
	mirrored?: boolean;
	className?: string;
	objectFit?: 'cover' | 'contain';
}) => {
	const ref = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		const node = ref.current;
		if (!node) return;

		node.srcObject = active && stream ? stream : null;
	}, [active, stream]);

	if (!active || !stream) return null;

	return (
		<video
			ref={ref}
			autoPlay
			muted
			playsInline
			className={cn('absolute inset-0 size-full', className)}
			style={{
				objectFit,
				transform: mirrored ? 'scale(-1, 1)' : undefined,
				backgroundColor: DAILY_PANEL_BG,
			}}
		/>
	);
};

const LocalParticipantTile = ({
	name,
	stream,
	showVideo,
	isScreen,
	className,
	highlightBorder = false,
}: {
	name: string;
	stream: MediaStream | null;
	showVideo: boolean;
	isScreen?: boolean;
	className?: string;
	highlightBorder?: boolean;
}) => (
	<div
		className={cn(
			'relative box-border flex min-h-0 min-w-0 items-center justify-center overflow-hidden',
			className,
		)}
		style={{
			backgroundColor: showVideo ? DAILY_PANEL_BG : DAILY_SURFACE,
			...(highlightBorder
				? { border: `1px solid ${DAILY_PANEL_BG}` }
				: undefined),
		}}
	>
		<LocalMediaVideo
			stream={stream}
			active={showVideo}
			mirrored={!isScreen}
			objectFit="contain"
		/>
		{!showVideo ? (
			<p className="px-3 text-center text-sm font-medium text-white/90">
				{name} <span className="text-white/70">(Você)</span>
			</p>
		) : (
			<TileNameBadge
				name={name}
				isLocal
				extra={isScreen ? ' · Compartilhando tela' : undefined}
			/>
		)}
	</div>
);

type Props = {
	participantDisplayName: string;
	mockPatientDisplayName: string;
	mockPatientTileLabel: string;
	isProntuarioOpen: boolean;
	onToggleProntuario: () => void;
	onEndDemonstration: (options?: { withTutorialCoach?: boolean }) => void;
	isEndConfirmOpen?: boolean;
	stream: MediaStream | null;
	screenStream: MediaStream | null;
	isScreenSharing: boolean;
	camOn: boolean;
	micOn: boolean;
	hasCamera: boolean;
	hasMicrophone: boolean;
	onToggleCamera: () => void;
	onToggleMicrophone: () => void;
	onStartScreenShare: () => void;
	onStopScreenShare: () => void;
	registerProntuarioDemoBridge?: (bridge: ProntuarioDemoBridge) => void;
};

export type ProntuarioDemoBridge = {
	onPatientCardOpen: () => void;
	onSessionsOpen: () => void;
	onMinimize: () => void;
	onViewChange: (view: SessionCallProntuarioView) => void;
	onEndConfirmComplete: () => void;
};

type CallCoachStep =
	| 'welcome'
	| 'devices'
	| 'tray'
	| 'timer'
	| 'prontuario'
	| 'prontuario-sessions'
	| 'prontuario-patient'
	| 'prontuario-notes'
	| 'end-call'
	| 'end-confirm'
	| null;

const PRONTUARIO_TOUR_COACH_STEPS = [
	'prontuario-sessions',
	'prontuario-patient',
	'prontuario-notes',
] as const satisfies readonly CallCoachStep[];

const isProntuarioTourCoachStep = (
	step: CallCoachStep,
): step is (typeof PRONTUARIO_TOUR_COACH_STEPS)[number] =>
	PRONTUARIO_TOUR_COACH_STEPS.includes(
		step as (typeof PRONTUARIO_TOUR_COACH_STEPS)[number],
	);

const isProntuarioMinimizeCoachPhase = (step: CallCoachStep) =>
	isProntuarioTourCoachStep(step);

const PRONTUARIO_PANEL_COACH_PROPS = {
	backdropTargetSelector: TUTORIAL_DEMO_PRONTUARIO_CONTENT_SELECTOR,
	showTargetSpotlight: false,
	allowTargetInteraction: true,
	additionalInteractionSelectors: [
		TUTORIAL_DEMO_PRONTUARIO_FORMATTING_HELP_SELECTOR,
	],
} as const;

const DailyCallDemonstracaoMock = ({
	participantDisplayName,
	mockPatientDisplayName,
	mockPatientTileLabel,
	isProntuarioOpen,
	onToggleProntuario,
	onEndDemonstration,
	isEndConfirmOpen = false,
	stream,
	screenStream,
	isScreenSharing,
	camOn,
	micOn,
	hasCamera,
	hasMicrophone,
	onToggleCamera,
	onToggleMicrophone,
	onStartScreenShare,
	onStopScreenShare,
	registerProntuarioDemoBridge,
}: Props) => {
	const cameraLabel = !hasCamera
		? 'Sem câmera'
		: camOn
		  ? 'Desligar'
		  : 'Ativar';
	const micLabel = !hasMicrophone
		? 'Sem microfone'
		: micOn
		  ? 'Silenciar'
		  : 'Ativar';
	const showingScreen = isScreenSharing && screenStream;
	const showingCamera = Boolean(!showingScreen && camOn && stream);

	const [layoutMode, setLayoutMode] = useState<LayoutMode>('presenter');
	const [sidePanel, setSidePanel] = useState<DailyCallSidePanel>(null);
	const [callCoachStep, setCallCoachStep] =
		useState<CallCoachStep>('welcome');
	const [prontuarioView, setProntuarioView] =
		useState<SessionCallProntuarioView>('notes');
	const [hasVisitedSessionsInTour, setHasVisitedSessionsInTour] =
		useState(false);
	const [hasVisitedPatientDetailInTour, setHasVisitedPatientDetailInTour] =
		useState(false);
	const [showPatientDetailCoach, setShowPatientDetailCoach] = useState(false);
	const [showSessionsCoach, setShowSessionsCoach] = useState(false);
	const [showMinimizeCoach, setShowMinimizeCoach] = useState(false);
	const [hasShownMinimizeCoach, setHasShownMinimizeCoach] = useState(false);
	const [pendingEndCallCoach, setPendingEndCallCoach] = useState(false);
	const [prontuarioTourResumeKey, setProntuarioTourResumeKey] = useState(0);
	const deviceControlsRef = useRef<HTMLDivElement>(null);
	const trayControlsRef = useRef<HTMLDivElement>(null);
	const prevIsProntuarioOpenRef = useRef(isProntuarioOpen);

	const toggleSidePanel = useCallback(
		(panel: Exclude<DailyCallSidePanel, null>) => {
			setSidePanel((current) => (current === panel ? null : panel));
		},
		[],
	);

	const closeSidePanel = useCallback(() => {
		setSidePanel(null);
	}, []);

	const handleShareClick = useCallback(() => {
		if (isScreenSharing) {
			onStopScreenShare();
			return;
		}

		void onStartScreenShare();
	}, [isScreenSharing, onStartScreenShare, onStopScreenShare]);

	const finishCallCoach = useCallback(() => {
		setCallCoachStep(null);
		setPendingEndCallCoach(false);
	}, []);

	const handleEndDemonstrationClick = useCallback(() => {
		const shouldCoachEndConfirmModal =
			callCoachStep === 'end-call' || pendingEndCallCoach;

		if (shouldCoachEndConfirmModal) {
			setPendingEndCallCoach(false);
			setCallCoachStep('end-confirm');
			onEndDemonstration({ withTutorialCoach: true });
			return;
		}

		finishCallCoach();
		onEndDemonstration({ withTutorialCoach: false });
	}, [
		callCoachStep,
		finishCallCoach,
		onEndDemonstration,
		pendingEndCallCoach,
	]);

	const advanceToProntuarioTourStep = useCallback(() => {
		setCallCoachStep('prontuario-sessions');
		if (!isProntuarioOpen) {
			onToggleProntuario();
		}
	}, [isProntuarioOpen, onToggleProntuario]);

	const getNextProntuarioTourStepAfterSessions = useCallback(
		() => 'prontuario-patient' as const,
		[],
	);

	const getNextProntuarioTourStepAfterPatient = useCallback(
		() => 'prontuario-notes' as const,
		[],
	);

	const advanceFromProntuarioSessionsStep = useCallback(() => {
		setCallCoachStep(getNextProntuarioTourStepAfterSessions());
	}, [getNextProntuarioTourStepAfterSessions]);

	const handleDemoPatientCardOpen = useCallback(() => {
		if (callCoachStep !== 'prontuario-patient') return;

		setHasVisitedPatientDetailInTour(true);
		setShowPatientDetailCoach(true);
	}, [callCoachStep]);

	const handleDemoSessionsOpen = useCallback(() => {
		if (!isProntuarioTourCoachStep(callCoachStep)) return;

		setHasVisitedSessionsInTour(true);
		setShowSessionsCoach(true);
	}, [callCoachStep]);

	const handleDemoMinimize = useCallback(() => {
		if (!isProntuarioMinimizeCoachPhase(callCoachStep)) return;

		if (!hasShownMinimizeCoach) {
			setHasShownMinimizeCoach(true);
			setShowMinimizeCoach(true);
			return;
		}

		setShowMinimizeCoach(false);
		setPendingEndCallCoach(true);
	}, [callCoachStep, hasShownMinimizeCoach]);

	const finishMinimizeCoachAndAdvance = useCallback(() => {
		setShowMinimizeCoach(false);
		setPendingEndCallCoach(true);
	}, []);

	const dismissMinimizeCoach = useCallback(() => {
		setShowMinimizeCoach(false);
	}, []);

	const handleDemoViewChange = useCallback(
		(view: SessionCallProntuarioView) => {
			setProntuarioView(view);

			if (view !== 'patient-detail') {
				setShowPatientDetailCoach(false);
			}

			if (view !== 'sessions') {
				setShowSessionsCoach(false);
			}

			if (view === 'notes' && callCoachStep === 'prontuario-sessions') {
				setCallCoachStep(getNextProntuarioTourStepAfterSessions());
			}
		},
		[callCoachStep, getNextProntuarioTourStepAfterSessions],
	);

	useEffect(() => {
		registerProntuarioDemoBridge?.({
			onPatientCardOpen: handleDemoPatientCardOpen,
			onSessionsOpen: handleDemoSessionsOpen,
			onMinimize: handleDemoMinimize,
			onViewChange: handleDemoViewChange,
			onEndConfirmComplete: finishCallCoach,
		});
	}, [
		finishCallCoach,
		handleDemoPatientCardOpen,
		handleDemoSessionsOpen,
		handleDemoMinimize,
		handleDemoViewChange,
		registerProntuarioDemoBridge,
	]);

	useEffect(() => {
		if (!isEndConfirmOpen && callCoachStep === 'end-confirm') {
			setCallCoachStep('end-call');
		}
	}, [callCoachStep, isEndConfirmOpen]);

	useEffect(() => {
		if (
			callCoachStep === 'prontuario-sessions' &&
			hasVisitedSessionsInTour &&
			prontuarioView === 'notes'
		) {
			setCallCoachStep(getNextProntuarioTourStepAfterSessions());
		}
	}, [
		callCoachStep,
		getNextProntuarioTourStepAfterSessions,
		hasVisitedSessionsInTour,
		prontuarioView,
	]);

	useEffect(() => {
		if (
			callCoachStep === 'prontuario-patient' &&
			hasVisitedPatientDetailInTour &&
			prontuarioView === 'notes'
		) {
			setCallCoachStep(getNextProntuarioTourStepAfterPatient());
		}
	}, [
		callCoachStep,
		getNextProntuarioTourStepAfterPatient,
		hasVisitedPatientDetailInTour,
		prontuarioView,
	]);

	useEffect(() => {
		if (
			isProntuarioTourCoachStep(callCoachStep) &&
			!isProntuarioOpen &&
			!showMinimizeCoach &&
			!hasShownMinimizeCoach
		) {
			onToggleProntuario();
		}
	}, [
		callCoachStep,
		hasShownMinimizeCoach,
		isProntuarioOpen,
		onToggleProntuario,
		showMinimizeCoach,
	]);

	useEffect(() => {
		const wasOpen = prevIsProntuarioOpenRef.current;
		prevIsProntuarioOpenRef.current = isProntuarioOpen;

		if (wasOpen || !isProntuarioOpen) return;
		if (!isProntuarioTourCoachStep(callCoachStep)) return;

		dismissMinimizeCoach();
		setProntuarioTourResumeKey((current) => current + 1);
	}, [callCoachStep, dismissMinimizeCoach, isProntuarioOpen]);

	const prontuarioTourCoachKey = `prontuario-tour-${callCoachStep}-${prontuarioTourResumeKey}`;
	const shouldShowEndCallCoach =
		!showMinimizeCoach &&
		callCoachStep !== 'end-confirm' &&
		(callCoachStep === 'end-call' ||
			(pendingEndCallCoach && !isProntuarioOpen));

	const renderStage = () => {
		if (layoutMode === 'grid') {
			if (showingScreen) {
				return (
					<div className="grid size-full min-h-0 grid-rows-2">
						<LocalParticipantTile
							name={participantDisplayName}
							stream={screenStream}
							showVideo
							isScreen
							className="size-full min-h-0"
						/>
						<LocalParticipantTile
							name={participantDisplayName}
							stream={stream}
							showVideo={showingCamera}
							className="size-full min-h-0"
						/>
					</div>
				);
			}

			return (
				<div className="grid size-full min-h-0 grid-rows-2">
					<RemoteParticipantTile
						centerLabel={mockPatientTileLabel}
						badgeName={mockPatientDisplayName}
						className="size-full min-h-0"
					/>
					<LocalParticipantTile
						name={participantDisplayName}
						stream={stream}
						showVideo={showingCamera}
						className="size-full min-h-0"
					/>
				</div>
			);
		}

		if (showingScreen) {
			return (
				<div className="relative size-full min-h-0 overflow-hidden">
					<LocalParticipantTile
						name={participantDisplayName}
						stream={screenStream}
						showVideo
						isScreen
						className="absolute inset-0 size-full"
					/>
					<RemoteParticipantTile
						centerLabel={mockPatientTileLabel}
						badgeName={mockPatientDisplayName}
						className={LOCAL_PIP_TILE_CLASS}
					/>
				</div>
			);
		}

		return (
			<div className="relative size-full min-h-0 overflow-hidden">
				<RemoteParticipantTile
					centerLabel={mockPatientTileLabel}
					badgeName={mockPatientDisplayName}
					className="absolute inset-0 size-full"
				/>
				<LocalParticipantTile
					name={participantDisplayName}
					stream={stream}
					showVideo={showingCamera}
					highlightBorder
					className={LOCAL_PIP_TILE_CLASS}
				/>
			</div>
		);
	};

	return (
		<div
			className="flex h-full min-h-0 w-full flex-col text-white"
			style={{ backgroundColor: DAILY_PANEL_BG }}
		>
			{callCoachStep === 'welcome' ? (
				<TutorialDemonstracaoCallWelcomeCoachBalloon
					onContinue={() => setCallCoachStep('devices')}
				/>
			) : null}
			{callCoachStep === 'devices' ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetRef={deviceControlsRef}
					bodyId="demo-call-coach-devices"
					onContinue={() => setCallCoachStep('tray')}
				>
					Aqui você controla os dispositivos de áudio e vídeo. Ligue
					ou desligue a câmera e o microfone conforme precisar durante
					a sessão.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{callCoachStep === 'tray' ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetRef={trayControlsRef}
					bodyId="demo-call-coach-tray"
					balloonWidthPx={400}
					compact
					onContinue={() => setCallCoachStep('timer')}
				>
					Aqui ficam os controles principais da sessão: participantes,
					chat, compartilhamento de tela, prontuário e encerramento.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{callCoachStep === 'timer' ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetSelector={TUTORIAL_DEMO_MEETING_TIMER_SELECTOR}
					bodyId="demo-call-coach-timer"
					balloonWidthPx={300}
					placement="below"
					allowTargetInteraction
					onContinue={() => setCallCoachStep('prontuario')}
				>
					Aqui você acompanha o tempo da sessão em tempo real — o
					contador inicia automaticamente no horário agendado.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{callCoachStep === 'prontuario' ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetSelectors={TUTORIAL_DEMO_PRONTUARIO_OPEN_SELECTORS}
					bodyId="demo-call-coach-prontuario"
					balloonWidthPx={380}
					compact
					allowTargetInteraction
					onTargetContinue={advanceToProntuarioTourStep}
					onContinue={advanceToProntuarioTourStep}
				>
					Você pode abrir o prontuário de duas formas: pelo botão na
					barra inferior ou pela aba flutuante à direita. A IA
					transcreve e registra a sessão enquanto você atende.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{callCoachStep === 'prontuario-sessions' &&
			isProntuarioOpen &&
			prontuarioView === 'notes' &&
			!hasVisitedSessionsInTour &&
			!showPatientDetailCoach &&
			!showSessionsCoach ? (
				<TutorialDemonstracaoCallCoachBalloon
					key={prontuarioTourCoachKey}
					{...PRONTUARIO_PANEL_COACH_PROPS}
					targetSelector={
						TUTORIAL_DEMO_PRONTUARIO_SESSIONS_OPEN_SELECTOR
					}
					bodyId="demo-call-coach-prontuario-sessions"
					balloonWidthPx={320}
					placement="left"
					compact
					onContinue={advanceFromProntuarioSessionsStep}
				>
					Clique aqui para ver o histórico de sessões anteriores do
					paciente.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{callCoachStep === 'prontuario-patient' &&
			isProntuarioOpen &&
			prontuarioView === 'notes' &&
			!hasVisitedPatientDetailInTour &&
			!showPatientDetailCoach &&
			!showSessionsCoach ? (
				<TutorialDemonstracaoCallCoachBalloon
					key={prontuarioTourCoachKey}
					{...PRONTUARIO_PANEL_COACH_PROPS}
					targetSelector={
						TUTORIAL_DEMO_PRONTUARIO_PATIENT_CARD_SELECTOR
					}
					bodyId="demo-call-coach-prontuario-patient"
					balloonWidthPx={320}
					placement="left"
					compact
					onContinue={() =>
						setCallCoachStep(
							getNextProntuarioTourStepAfterPatient(),
						)
					}
				>
					Clicando no card você acessa todas as informações do
					paciente.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{showPatientDetailCoach &&
			isProntuarioOpen &&
			prontuarioView === 'patient-detail' &&
			isProntuarioTourCoachStep(callCoachStep) ? (
				<TutorialDemonstracaoCallCoachBalloon
					key={`${prontuarioTourCoachKey}-patient-detail`}
					{...PRONTUARIO_PANEL_COACH_PROPS}
					targetSelector={
						TUTORIAL_DEMO_PRONTUARIO_PATIENT_DETAIL_SELECTOR
					}
					bodyId="demo-call-coach-prontuario-patient-detail"
					balloonWidthPx={340}
					placement="left"
					compact
					showContinueButton={false}
					onContinue={() => undefined}
				>
					Aqui você consulta os dados completos do paciente —
					informações pessoais, histórico clínico e mais. Use o botão
					de voltar no topo para retornar às anotações.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{showSessionsCoach &&
			isProntuarioOpen &&
			prontuarioView === 'sessions' &&
			isProntuarioTourCoachStep(callCoachStep) ? (
				<TutorialDemonstracaoCallCoachBalloon
					key={`${prontuarioTourCoachKey}-sessions-list`}
					{...PRONTUARIO_PANEL_COACH_PROPS}
					targetSelector={
						TUTORIAL_DEMO_PRONTUARIO_SESSIONS_LIST_SELECTOR
					}
					bodyId="demo-call-coach-prontuario-sessions-list"
					balloonWidthPx={340}
					placement="left"
					compact
					showContinueButton={false}
					onContinue={() => undefined}
				>
					Aqui você consulta todas as sessões anteriores e pode
					revisar as anotações de cada uma. Clique na sessão atual
					para voltar às anotações.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{callCoachStep === 'prontuario-notes' &&
			isProntuarioOpen &&
			prontuarioView === 'notes' &&
			!showPatientDetailCoach &&
			!showSessionsCoach ? (
				<TutorialDemonstracaoCallCoachBalloon
					key={prontuarioTourCoachKey}
					{...PRONTUARIO_PANEL_COACH_PROPS}
					targetSelector={TUTORIAL_DEMO_PRONTUARIO_NOTES_SELECTOR}
					bodyId="demo-call-coach-prontuario-notes"
					balloonWidthPx={340}
					placement="left"
					onContinue={() => {
						setPendingEndCallCoach(false);
						setCallCoachStep('end-call');
					}}
				>
					Este campo aqui é seu espaço para anotar à vontade — tudo
					salvo automaticamente!
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{showMinimizeCoach &&
			!isProntuarioOpen &&
			isProntuarioMinimizeCoachPhase(callCoachStep) ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetSelectors={TUTORIAL_DEMO_PRONTUARIO_OPEN_SELECTORS}
					bodyId="demo-call-coach-prontuario-minimize"
					balloonWidthPx={380}
					placement="above"
					compact
					allowTargetInteraction
					allowHeaderInteraction={false}
					onContinue={finishMinimizeCoachAndAdvance}
				>
					Lembre-se: o prontuário fica disponível a qualquer momento
					pelo botão na barra inferior ou pela aba à direita.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			{shouldShowEndCallCoach ? (
				<TutorialDemonstracaoCallCoachBalloon
					targetSelector={TUTORIAL_DEMO_END_CALL_SELECTOR}
					bodyId="demo-call-coach-end-call"
					balloonWidthPx={380}
					placement="above"
					compact
					showBackdrop={false}
					showTargetSpotlight={false}
					showContinueButton={false}
					onContinue={finishCallCoach}
				>
					Fique à vontade para explorar. Quando estiver pronto para
					continuar e ver o resultado da reunião e o prontuário final
					pré-preenchido, clique aqui no botão{' '}
					<Power
						className="mx-0.5 inline size-3.5 align-text-bottom text-[#ea4335]"
						strokeWidth={2.5}
						aria-hidden
					/>{' '}
					Encerrar.
				</TutorialDemonstracaoCallCoachBalloon>
			) : null}
			<div
				className="flex shrink-0 items-center justify-between gap-4 px-4"
				style={{
					height: 33,
					minHeight: 33,
					backgroundColor: DAILY_PANEL_BG,
					borderBottom: `1px solid ${DAILY_SURFACE}`,
				}}
			>
				<div className="flex flex-1 items-center gap-1.5 text-xs text-white/90">
					<Users className="size-3.5 shrink-0" strokeWidth={2.25} />
					<span>2 pessoas na chamada</span>
				</div>

				<div
					role="radiogroup"
					className="flex shrink-0 items-center gap-2"
					aria-label="Modo de exibição da chamada"
				>
					<span className="hidden text-xs text-white/90 sm:inline">
						{layoutMode === 'presenter'
							? 'Modo de exibição do apresentador'
							: 'Modo de exibição em grade'}
					</span>
					<div className="flex items-center gap-1">
						<button
							type="button"
							id="view-switch-speaker"
							role="radio"
							onClick={() => setLayoutMode('presenter')}
							aria-label="Alternar para o modo de exibição de destaque de apresentador"
							aria-checked={layoutMode === 'presenter'}
							className={cn(
								'flex size-7 items-center justify-center rounded-md transition-colors',
								layoutMode === 'presenter'
									? 'text-[#121A24]'
									: 'text-white',
							)}
							style={{
								backgroundColor:
									layoutMode === 'presenter'
										? PRONTUARIO_TRAY_ACTIVE_COLOR
										: DAILY_LAYOUT_INACTIVE_BG,
							}}
						>
							<DailyPresenterLayoutIcon />
						</button>
						<button
							type="button"
							id="view-switch-grid"
							role="radio"
							onClick={() => setLayoutMode('grid')}
							aria-label="Alternar para o modo de exibição em grade"
							aria-checked={layoutMode === 'grid'}
							className={cn(
								'flex size-7 items-center justify-center rounded-md transition-colors',
								layoutMode === 'grid'
									? 'text-[#121A24]'
									: 'text-white',
							)}
							style={{
								backgroundColor:
									layoutMode === 'grid'
										? PRONTUARIO_TRAY_ACTIVE_COLOR
										: DAILY_LAYOUT_INACTIVE_BG,
							}}
						>
							<DailyGridLayoutIcon />
						</button>
					</div>
				</div>
			</div>

			<div className="flex min-h-0 flex-1 overflow-hidden">
				<div
					className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden"
					style={{ backgroundColor: DAILY_SURFACE }}
				>
					{renderStage()}
				</div>

				{sidePanel === 'people' || sidePanel === 'chat' ? (
					<DailyCallDemonstracaoPeopleChatPanel
						participantDisplayName={participantDisplayName}
						mockPatientDisplayName={mockPatientDisplayName}
						micOn={micOn}
						camOn={camOn}
						hasCamera={hasCamera}
						hasMicrophone={hasMicrophone}
						activeTab={sidePanel}
						onTabChange={(tab) => setSidePanel(tab)}
						onClose={closeSidePanel}
					/>
				) : null}
			</div>

			<div
				className="flex h-[59px] shrink-0 items-center px-2 sm:px-3"
				style={{
					backgroundColor: DAILY_PANEL_BG,
					borderTop: `1px solid ${DAILY_SURFACE}`,
				}}
			>
				<div className="flex min-w-0 flex-1 items-center justify-start pl-1">
					<div
						ref={deviceControlsRef}
						className="flex shrink-0 items-center gap-1"
					>
						<DailyDeviceTrayButton
							label={cameraLabel}
							variant="camera"
							isOn={camOn && hasCamera}
							onClick={onToggleCamera}
						/>
						<DailyDeviceTrayButton
							label={micLabel}
							variant="mic"
							isOn={micOn && hasMicrophone}
							onClick={onToggleMicrophone}
						/>
					</div>
				</div>

				<div
					ref={trayControlsRef}
					className="flex shrink-0 items-center justify-center gap-0.5 sm:gap-1"
				>
					<DailyTrayButton
						compact
						label="Pessoas"
						isActive={sidePanel === 'people'}
						onClick={() => toggleSidePanel('people')}
					>
						<Users className="size-5" strokeWidth={2.25} />
					</DailyTrayButton>
					<DailyTrayButton
						compact
						label="Bate-papo"
						isActive={sidePanel === 'chat'}
						onClick={() => toggleSidePanel('chat')}
					>
						<MessageSquare className="size-5" strokeWidth={2.25} />
					</DailyTrayButton>
					<DailyTrayButton
						compact
						label={isScreenSharing ? 'Parar' : 'Compartilhar'}
						isActive={isScreenSharing}
						onClick={handleShareClick}
					>
						<MonitorUp className="size-5" strokeWidth={2.25} />
					</DailyTrayButton>
					<DailyTrayButton
						compact
						label="Prontuário"
						isActive={isProntuarioOpen}
						onClick={onToggleProntuario}
						tutorialDemoTarget="prontuario-open-tray"
					>
						<span
							className={cn(
								'flex size-6 items-center justify-center rounded',
								isProntuarioOpen && 'bg-[rgb(27,235,185)]/20',
							)}
						>
							<img
								src={prontuarioTrayIconDataUri}
								alt=""
								className="size-5"
								aria-hidden
							/>
						</span>
					</DailyTrayButton>
					<DailyTrayButton
						compact
						label="Encerrar"
						danger
						onClick={handleEndDemonstrationClick}
						tutorialDemoTarget="end-call"
					>
						<Power className="size-5" strokeWidth={2.5} />
					</DailyTrayButton>
				</div>

				<div className="flex-1" aria-hidden />
			</div>
		</div>
	);
};

export { DailyCallDemonstracaoMock };
